import { createEffect, Show } from "solid-js"
import { Portal } from "solid-js/web"
import { Button } from "@opencode/ui/button"
import { Logo } from "@opencode/ui/logo"
import { AppInterface } from "@/app"
import { SairSignIn } from "@/sair/gate"
import { SairSignInProgress } from "@/sair/sign-in-progress"
import { useSairEntry } from "@/sair/entry-context"
import { useLanguage } from "@/runtime/i18n/language"
import { clearPwaRoute, PwaRoutePersistence, restorePwaRoute } from "@/runtime/platform/pwa"
import { KeyboardInsets } from "@/runtime/platform/keyboard"
import { ServerConnection } from "./registry"
import { CloudWorkspaceContext, type CloudWorkspace } from "./cloud-context"
import { createCloudApi } from "./cloud-api"
import { createCloudWorkspace } from "./cloud"
import { createCloudSignIn } from "./cloud-sign-in"

const clientKey = "omm-workspace-client"
const identityKey = "omm.cloud.last-identity"

export function CloudApp(props: { standalone: boolean; gatewayOrigin: string }) {
  const entry = useSairEntry()
  const cloud = createCloudWorkspace({
    api: createCloudApi({ origin: props.gatewayOrigin, browserOrigin: location.origin }),
    origin: props.gatewayOrigin,
    clientID: browserClientID(),
    signIn: createCloudSignIn({
      url: () => new URL(location.href),
      local: () => localStorage,
      session: () => sessionStorage,
      navigate: (url) => location.assign(url),
      replace: (path) => history.replaceState(null, "", path),
    }),
    onSignedOut: () => {
      const identity = read(identityKey)
      if (identity) clearPwaRoute(identity)
      history.replaceState(null, "", "/")
    },
  })
  const completing = () =>
    cloud.state.phase === "authorizing" &&
    !!cloud.account.state.status?.attempt &&
    !cloud.account.state.status?.error &&
    !cloud.account.state.loadError &&
    !cloud.account.state.actionError
  const failed = () => !cloud.state.busy && cloud.state.phase === "unavailable"
  const opening = () => cloud.state.busy || !cloud.state.connection
  createEffect(() => {
    entry.report(
      cloud.state.connection && !opening() && !failed()
        ? "ready"
        : cloud.account.state.status?.profile
          ? failed()
            ? "recovery"
            : "pending"
          : cloud.account.state.loadError
            ? "recovery"
            : cloud.account.state.status?.attempt
              ? "pending"
              : cloud.account.state.status
                ? "sign-in"
                : "checking",
    )
  })
  return (
    <CloudWorkspaceContext.Provider value={cloud}>
      <Show when={cloud.state.connection?.key} keyed>
        {(key) => (
          <CloudReady cloud={cloud} identity={key} standalone={props.standalone} gatewayOrigin={props.gatewayOrigin} />
        )}
      </Show>
      <Show when={(opening() || failed()) && entry.panel()}>
        {(panel) => (
          <Portal mount={panel()}>
            <Show
              when={cloud.account.state.status?.profile}
              fallback={
                <Show
                  when={completing()}
                  fallback={<SairSignIn account={cloud.account} web completing={cloud.state.phase === "authorizing"} />}
                >
                  <SairSignInProgress account={cloud.account} />
                </Show>
              }
            >
              <Show when={failed()} fallback={<SairSignInProgress account={cloud.account} workspace />}>
                <CloudWorkspaceRecovery cloud={cloud} />
              </Show>
            </Show>
          </Portal>
        )}
      </Show>
    </CloudWorkspaceContext.Provider>
  )
}

function CloudWorkspaceRecovery(props: { cloud: CloudWorkspace }) {
  const language = useLanguage()
  const description = () => {
    if (props.cloud.state.error === "workspace_access_denied") return "sair.access.workspace.description"
    if (props.cloud.state.error === "omm_access_denied") return "sair.access.denied.description"
    if (props.cloud.state.error === "timeout") return "cloud.workspace.timeout"
    if (props.cloud.state.error === "network") return "cloud.workspace.network"
    if (props.cloud.state.error === "aborted") return "cloud.workspace.cancelled.description"
    return "cloud.workspace.failed"
  }
  return (
    <section class="sair-sign-in-content sair-cloud-status" data-testid="cloud-workspace-recovery">
      <div class="sair-sign-in-wordmark" role="img" aria-label={language.t("sair.logo")}>
        <Logo />
      </div>
      <div class="sair-sign-in-progress-body">
        <div class="sair-sign-in-progress-title">
          <h1>
            {language.t(
              props.cloud.state.error === "aborted" ? "cloud.workspace.cancelled.title" : "cloud.workspace.failedTitle",
            )}
          </h1>
        </div>
        <p role="alert">{language.t(description())}</p>
        <Show when={props.cloud.state.errorMessage}>
          {(message) => <p class="sair-workspace-error-details">{message()}</p>}
        </Show>
        <div class="sair-sign-in-secondary sair-workspace-recovery-actions">
          <Button
            variant="contrast"
            disabled={props.cloud.account.state.busy}
            onClick={() => void props.cloud.ensureRemote(undefined, true).catch(() => undefined)}
          >
            {language.t("sair.retry")}
          </Button>
          <Button
            variant="ghost"
            title={language.t("cloud.workspace.signOut")}
            disabled={props.cloud.account.state.busy}
            onClick={() => void props.cloud.account.logout()}
          >
            {language.t("sair.logout.action")}
          </Button>
        </div>
      </div>
    </section>
  )
}

function CloudReady(props: { cloud: CloudWorkspace; identity: string; standalone: boolean; gatewayOrigin: string }) {
  const language = useLanguage()
  const previous = read(identityKey)
  if (previous && previous !== props.identity) history.replaceState(null, "", "/")
  write(identityKey, props.identity)
  if (props.standalone) restorePwaRoute(props.identity)
  const server: ServerConnection.Remote = {
    type: "remote",
    id: props.identity,
    displayName: language.t("remote.remote"),
    http: { url: props.gatewayOrigin },
    enabled: () => props.cloud.state.phase === "ready",
    reconnect: props.cloud.reconnect,
  }
  return (
    <AppInterface servers={[server]} defaultServer={ServerConnection.key(server)} exclusiveServers>
      <KeyboardInsets />
      {props.standalone && <PwaRoutePersistence identity={props.identity} />}
    </AppInterface>
  )
}

function browserClientID() {
  const stored = read(clientKey)
  if (stored && /^[a-zA-Z0-9_-]{1,128}$/.test(stored)) return stored
  const value = crypto.randomUUID()
  write(clientKey, value)
  return value
}

function read(key: string) {
  try {
    return localStorage.getItem(key)
  } catch {
    return null
  }
}

function write(key: string, value: string) {
  try {
    localStorage.setItem(key, value)
  } catch {
    /* Cookie authentication does not require local storage. */
  }
}
